import {
  ActivityResponse,
  ConsignmentResponse,
  CustomerOrderResponse,
  HumanMeanResponse,
  InvoicingDocumentResponse,
  ItemResponse,
  MaterialMeanResponse,
  PieceResponse,
  ProductResponse,
  QuotationRequestResponse,
  QuotationResponse,
  ServiceRequestResponse,
  ServiceResponse,
} from '@/api/bol';

export type LogisticsObjectData =
  | ActivityResponse
  | ConsignmentResponse
  | CustomerOrderResponse
  | HumanMeanResponse
  | InvoicingDocumentResponse
  | ItemResponse
  | MaterialMeanResponse
  | PieceResponse
  | ProductResponse
  | QuotationRequestResponse
  | QuotationResponse
  | ServiceRequestResponse
  | ServiceResponse;

export const LOGISTICS_OBJECT_ENTITIES = [
  'activity',
  'consignment',
  'customer-order',
  'human-mean',
  'invoicing-document',
  'item',
  'material-mean',
  'piece',
  'product',
  'quotation-request',
  'quotation',
  'service-request',
  'service',
] as const;

export type LogisticsObjectEntity = typeof LOGISTICS_OBJECT_ENTITIES[number];

export const LOGISTICS_OBJECT_SCOPES = [
  ...LOGISTICS_OBJECT_ENTITIES,
  'all',
] as const;

export type LogisticsObjectScope = typeof LOGISTICS_OBJECT_SCOPES[number];

const LOGISTICS_OBJECT_ID_KEYS = {
  'activity': 'activityId',
  'consignment': 'consignmentId',
  'customer-order': 'customerOrderId',
  'human-mean': 'humanMeanId',
  'invoicing-document': 'invoicingDocumentId',
  'item': 'itemId',
  'material-mean': 'materialMeanId',
  'piece': 'pieceId',
  'product': 'productId',
  'quotation-request': 'quotationRequestId',
  'quotation': 'quotationId',
  'service-request': 'serviceRequestId',
  'service': 'serviceId',
} as const;

const LOGISTICS_OBJECT_ROUTE_SEGMENTS = {
  'activity': 'activities',
  'consignment': 'consignments',
  'customer-order': 'customer-orders',
  'human-mean': 'human-means',
  'invoicing-document': 'invoicing-documents',
  'item': 'items',
  'material-mean': 'material-means',
  'piece': 'pieces',
  'product': 'products',
  'quotation-request': 'quotation-requests',
  'quotation': 'quotations',
  'service-request': 'service-requests',
  'service': 'services',
} as const;

export type LogisticsObjectIdKey = typeof LOGISTICS_OBJECT_ID_KEYS[LogisticsObjectEntity];

export function getLogisticsObjectIdKey(entity: LogisticsObjectEntity): LogisticsObjectIdKey {
  return LOGISTICS_OBJECT_ID_KEYS[entity];
}

export function getLogisticsObjectDetailPath(entity: LogisticsObjectEntity, objectId: string): string {
  return `/${LOGISTICS_OBJECT_ROUTE_SEGMENTS[entity]}/${encodeURIComponent(objectId)}`;
}

const LOGISTICS_OBJECT_TYPE_TOKENS = {
  'activity': {
    singularPascal: 'Activity',
    pluralPascal: 'Activities',
    singularDisplay: 'Activity',
    pluralDisplay: 'Activities',
  },
  'consignment': {
    singularPascal: 'Consignment',
    pluralPascal: 'Consignments',
    singularDisplay: 'Consignment',
    pluralDisplay: 'Consignments',
  },
  'customer-order': {
    singularPascal: 'CustomerOrder',
    pluralPascal: 'CustomerOrders',
    singularDisplay: 'Customer Order',
    pluralDisplay: 'Customer Orders',
  },
  'human-mean': {
    singularPascal: 'HumanMean',
    pluralPascal: 'HumanMeans',
    singularDisplay: 'Human Mean',
    pluralDisplay: 'Human Means',
  },
  'invoicing-document': {
    singularPascal: 'InvoicingDocument',
    pluralPascal: 'InvoicingDocuments',
    singularDisplay: 'Invoicing Document',
    pluralDisplay: 'Invoicing Documents',
  },
  'item': {
    singularPascal: 'Item',
    pluralPascal: 'Items',
    singularDisplay: 'Item',
    pluralDisplay: 'Items',
  },
  'material-mean': {
    singularPascal: 'MaterialMean',
    pluralPascal: 'MaterialMeans',
    singularDisplay: 'Material Mean',
    pluralDisplay: 'Material Means',
  },
  'piece': {
    singularPascal: 'Piece',
    pluralPascal: 'Pieces',
    singularDisplay: 'Piece',
    pluralDisplay: 'Pieces',
  },
  'product': {
    singularPascal: 'Product',
    pluralPascal: 'Products',
    singularDisplay: 'Product',
    pluralDisplay: 'Products',
  },
  'quotation-request': {
    singularPascal: 'QuotationRequest',
    pluralPascal: 'QuotationRequests',
    singularDisplay: 'Quotation Request',
    pluralDisplay: 'Quotation Requests',
  },
  'quotation': {
    singularPascal: 'Quotation',
    pluralPascal: 'Quotations',
    singularDisplay: 'Quotation',
    pluralDisplay: 'Quotations',
  },
  'service-request': {
    singularPascal: 'ServiceRequest',
    pluralPascal: 'ServiceRequests',
    singularDisplay: 'Service Request',
    pluralDisplay: 'Service Requests',
  },
  'service': {
    singularPascal: 'Service',
    pluralPascal: 'Services',
    singularDisplay: 'Service',
    pluralDisplay: 'Services',
  },
} as const;

export type LogisticsObjectTypeTokens = typeof LOGISTICS_OBJECT_TYPE_TOKENS[LogisticsObjectEntity];

export function getLogisticsObjectTypeTokens(
  entity: LogisticsObjectEntity,
): LogisticsObjectTypeTokens {
  return LOGISTICS_OBJECT_TYPE_TOKENS[entity];
}
